import Accordion from "@/components/Accordion";
import React from "react";
import LogoUpload from "../../LogoUpload";
import ColorPicker from "@/components/ColorPicker";
import { StyleConfigModel } from "@/pages/Messaging/types";
import { Checkbox, LoadingSpinner } from "@velaro/velaro-component-lib";
import { ChatWindow } from "../../types";

interface Props {
  onExpanded(): void;
  workingChatWindow: ChatWindow;
  setWorkingChatWindow: (chatWindow: ChatWindow) => void;
  setStagedLogoFile: (file: File | null) => void;
}

export function Branding({
  onExpanded,
  workingChatWindow,
  setWorkingChatWindow,
  setStagedLogoFile
}: Props) {
  const workingStyleConfig = workingChatWindow.styleConfig || {};

  const updateStyleConfig = (updates: Partial<StyleConfigModel>) => {
    setWorkingChatWindow({
      ...workingChatWindow,
      styleConfig: {
        ...workingChatWindow.styleConfig,
        ...updates
      }
    });
  };

  return (
    <Accordion.Item eventKey="branding" onExpanded={() => onExpanded()}>
      <Accordion.Header>
        <Accordion.Title>Branding</Accordion.Title>
        <Accordion.SubTitle>
          Customize the chat window to reflect your brand identity
        </Accordion.SubTitle>
      </Accordion.Header>
      <Accordion.Body>
        <div>
          <LogoUpload
            setStageLogoFile={setStagedLogoFile}
            update={updateStyleConfig}
            workingStyleConfig={workingStyleConfig}
            field="logoUrl"
            label="Logo"
          />
          <div className="my-4 border-t border-slate-200" />
          <div className="text-sm font-medium mb-2">Color</div>
          <div className="grid grid-cols-3 items-center gap-4 mb-3">
            <div className="text-sm font-normal">Primary Color</div>
            <ColorPicker
              value={workingStyleConfig.backgroundColor}
              onChange={(value) =>
                updateStyleConfig({ backgroundColor: value })
              }
            />
            <div /> {/* Empty cell to match others' checkboxes */}
          </div>

          <div className="grid grid-cols-3 items-center gap-4 mb-3">
            <div className="text-sm font-normal">Header Text Color</div>
            <ColorPicker
              value={workingStyleConfig.headerTextColor || ""}
              onChange={(value) =>
                updateStyleConfig({ headerTextColor: value })
              }
              disabled={workingStyleConfig.useContrastedColorHeader}
            />
            <Checkbox
              checked={workingStyleConfig.useContrastedColorHeader}
              onClick={() =>
                updateStyleConfig({
                  useContrastedColorHeader:
                    !workingStyleConfig.useContrastedColorHeader
                })
              }
              label="Use Contrast Color"
            />
          </div>

          <div className="grid grid-cols-3 items-center gap-4 mb-3">
            <div className="text-sm font-normal">Body Text Color</div>
            <ColorPicker
              value={workingStyleConfig.bodyTextColor || ""}
              onChange={(value) => updateStyleConfig({ bodyTextColor: value })}
              disabled={workingStyleConfig.useContrastedColorBody}
            />
            <Checkbox
              checked={workingStyleConfig.useContrastedColorBody}
              onClick={() =>
                updateStyleConfig({
                  useContrastedColorBody:
                    !workingStyleConfig.useContrastedColorBody
                })
              }
              label="Use Contrast Color"
            />
          </div>

          <div className="my-4 border-t border-slate-200" />
          <div className="text-sm font-medium mb-2">Font</div>
          <select
            className="border border-slate-300 rounded-md p-2 w-full text-sm"
            value={workingStyleConfig.fontFamily ?? "inherit"}
            onChange={(e) => updateStyleConfig({ fontFamily: e.target.value })}
          >
            <option value="inherit">Default (inherit from page)</option>
            <option value="'Source Sans Pro', sans-serif">Source Sans Pro</option>
            <option value="'Inter', sans-serif">Inter</option>
            <option value="'Roboto', sans-serif">Roboto</option>
            <option value="'Open Sans', sans-serif">Open Sans</option>
            <option value="'Lato', sans-serif">Lato</option>
            <option value="'Poppins', sans-serif">Poppins</option>
            <option value="'Nunito', sans-serif">Nunito</option>
            <option value="Arial, sans-serif">Arial</option>
            <option value="Georgia, serif">Georgia</option>
          </select>
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
}
